import type { Router, LocationQuery } from "vue-router";

import { deepClone } from "@/utils";

interface Filter {
    key: string;
    value: string[];
}

interface DefaultFilter {
    key: string;
    value: string[];
}

interface URLManagerOptions {
    defaults?: DefaultFilter[];
    tracked?: string[];
    threshold?: number;
}

/**
 * Exports URLManager used process the state and sync the url
 */
export default class URLManager {
    private readonly $router: Router;
    private URLHistoryTimer: number;
    private opts!: Required<URLManagerOptions>;

    /**
     * URLManager constructor
     */
    constructor(router: Router, opts: URLManagerOptions = {}) {
        this.$router = router;
        this.URLHistoryTimer = Date.now();
        this.setOptions(opts);
    }

    /**
     * Returns the default options
     */
    static get defaults(): Required<URLManagerOptions> {
        return {
            defaults: [],
            tracked: [],
            threshold: 700
        };
    }

    /**
     * Returns the query object
     */
    get query(): LocationQuery {
        return this.$router.currentRoute.value?.query || {};
    }

    /**
     * Returns the query string from the url
     */
    get queryString(): string {
        // Encode param
        const mapParams = (key: string, values: string[]): string[] =>
            values.map(v => `${encodeURIComponent(key)}=${encodeURIComponent(String(v))}`);

        // Loop query object to build new string
        return Object.keys(this.query)
            .map(key => {
                const queryObj: LocationQuery = this.query;
                const val = queryObj[key];
                const arr = Array.isArray(val) ? val : [val];
                return mapParams(
                    key,
                    arr.filter((v): v is string => v !== null)
                ).join("&");
            })
            .join("&");
    }

    /**
     * Returns the first portion of the filter name as some have colons i.e. gv:location
     */
    static filterKey(key: string): string {
        return key.split(":")[0];
    }

    /**
     * Validates and sets the options
     */
    setOptions(opts: URLManagerOptions) {
        if (!opts || typeof opts !== "object") {
            throw new Error("URLManager expects an object to be passed");
        }

        const validateDefaults = () => {
            if (typeof opts.defaults !== "undefined") {
                if (!Array.isArray(opts.defaults)) {
                    throw new Error("URLManager expects defaults to be an Array");
                } else {
                    const valid = opts.defaults.every(
                        defaultFilter =>
                            defaultFilter &&
                            typeof defaultFilter === "object" &&
                            defaultFilter.key &&
                            defaultFilter.value
                    );

                    if (!valid) {
                        throw new Error(
                            "URLManager expects defaults to contain only objects with key and value properties"
                        );
                    }
                }
            }
        };

        const validateTracked = () => {
            if (typeof opts.tracked !== "undefined") {
                if (!Array.isArray(opts.tracked)) {
                    throw new Error("URLManager expects tracked to be an Array");
                } else {
                    const valid = opts.tracked.every(trackedFilter => typeof trackedFilter === "string");

                    if (!valid) {
                        throw new Error("URLManager expects tracked to contain only strings");
                    }
                }
            }
        };

        const validateThreshold = () => {
            if (typeof opts.threshold !== "undefined") {
                const threshold = parseInt(String(opts.threshold), 10);
                const valid = !Number.isNaN(threshold) && threshold >= 0;

                if (!valid) {
                    throw new Error("URLManager expects threshold to be a number greater than or equal to 0");
                }
            }
        };

        validateDefaults();
        validateTracked();
        validateThreshold();

        this.opts = { ...URLManager.defaults, ...opts };
    }

    /**
     * Returns the array of tracked parameters
     */
    trackedFilters(filters: Filter[] = []): string[] {
        // Reduce new array of tracked params
        return filters.reduce((tracked: string[], filter: Filter) => {
            // Push to tracked array is filter is supposed to be tracked
            if (this.opts.tracked.includes(URLManager.filterKey(filter.key))) {
                tracked.push(filter.key);
            }
            // Return the tracked array
            return tracked;
        }, []);
    }

    /**
     * Returns a default filter if the param value is the default value
     */
    isDefaultValue(filter: Filter) {
        const filterValue = (value: string[]) => JSON.stringify([...value].sort((a, b) => a.localeCompare(b)));
        return this.opts.defaults.find(
            defaultFilter =>
                defaultFilter.key === URLManager.filterKey(filter.key) &&
                filterValue(filter.value) === filterValue(defaultFilter.value)
        );
    }

    /**
     * Returns a the query string of the new state
     */
    getStateAsQueryString(params: Filter[]) {
        const filters = deepClone(params);
        const valid = filters.every(filter => filter && typeof filter === "object" && filter.key && filter.value);
        // Make sure passed params are a valid structure
        if (!valid) {
            throw new Error("URLManager expects filters to contain only objects with key and value properties");
        }
        // Get tracked filters
        const trackedFilters = this.trackedFilters(filters);
        const filterNotTracked = (key: string) => !trackedFilters.includes(key);
        // Loop filters and update state
        filters.forEach(filter => {
            // Filter not tracked so ignore
            if (filterNotTracked(filter.key)) {
                return;
            }
            // Delete filter if there are no values or the default value
            if (filter.value.length === 0 || this.isDefaultValue(filter)) {
                delete this.query[filter.key];
            } else if (filter.value.length === 1) {
                this.query[filter.key] = filter.value.pop()!;
            } else {
                this.query[filter.key] = filter.value;
            }
        });
        // Return new query string
        return this.queryString;
    }

    /**
     * Updates the URL with the new params from the changed state
     */
    async setURLParams(params: Filter[], replace = false) {
        // Store current query string for comparison
        const currentQueryString = this.queryString;

        // If the query string is not different then return
        if (currentQueryString === this.getStateAsQueryString(params)) {
            return;
        }

        // Get current timestamp to only update history when time is greater than threshold
        const now = Date.now();

        // Build new state for history
        const urlParams = this.queryString + window.location.hash;
        const url = (urlParams === "" ? document.location.pathname : "?") + urlParams;

        // Test whether the history of the browser should be updated or replaced
        if (this.URLHistoryTimer <= now || !replace) {
            await this.$router.push(url);
        }

        window.history.replaceState(window.history.state, "", url);

        // Update timer for next change
        this.URLHistoryTimer = now + this.opts.threshold;
    }
}
